import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text, Card, Chip, useTheme, ProgressBar } from 'react-native-paper';
import {
  TrendingUp,
  Users,
  Eye,
  ThumbsUp,
  Star,
  Target,
  BarChart3,
} from 'lucide-react-native';
import type { ContentRecommendation } from '@coffeeplexity/shared';

interface RecommendationAnalyticsData {
  viewCount: number;
  acceptanceRate: number;
  averageRating: number;
  similarUserEngagement: number;
  performanceScore: number;
}

interface RecommendationAnalyticsProps {
  data: RecommendationAnalyticsData;
  recommendation: ContentRecommendation;
}

export function RecommendationAnalytics({
  data,
  recommendation,
}: RecommendationAnalyticsProps) {
  const theme = useTheme();

  const getPerformanceColor = (score: number) => {
    if (score >= 0.8) return '#4ECDC4'; // High performance - Teal
    if (score >= 0.6) return '#FF6B35'; // Medium performance - Orange
    return '#E74C3C'; // Low performance - Red
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 4.0) return '#4ECDC4'; // Excellent
    if (rating >= 3.0) return '#FFA726'; // Good
    return '#FF7043'; // Needs improvement
  };

  const getEngagementLevel = (rate: number) => {
    if (rate >= 0.7) return 'High';
    if (rate >= 0.4) return 'Medium';
    return 'Low';
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  return (
    <Card
      style={[
        styles.container,
        { backgroundColor: theme.colors.surfaceVariant },
      ]}
    >
      <Card.Content style={styles.content}>
        {/* Header */}
        <View style={styles.header}>
          <BarChart3 size={16} color={theme.colors.primary} />
          <Text
            variant='labelMedium'
            style={[styles.title, { color: theme.colors.primary }]}
          >
            RECOMMENDATION ANALYTICS
          </Text>
        </View>

        {/* Performance Score */}
        <View style={styles.section}>
          <View style={styles.metricHeader}>
            <Target
              size={14}
              color={getPerformanceColor(data.performanceScore)}
            />
            <Text
              variant='labelSmall'
              style={[
                styles.metricLabel,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              Overall Performance
            </Text>
          </View>
          <View style={styles.performanceContainer}>
            <ProgressBar
              progress={data.performanceScore}
              color={getPerformanceColor(data.performanceScore)}
              style={styles.performanceBar}
            />
            <Text
              variant='bodySmall'
              style={[
                styles.performanceScore,
                { color: getPerformanceColor(data.performanceScore) },
              ]}
            >
              {Math.round(data.performanceScore * 100)}%
            </Text>
          </View>
        </View>

        {/* Key Metrics */}
        <View style={styles.metricsGrid}>
          {/* View Count */}
          <View style={styles.metric}>
            <View style={styles.metricIcon}>
              <Eye size={12} color={theme.colors.secondary} />
            </View>
            <Text
              variant='bodySmall'
              style={[styles.metricValue, { color: theme.colors.onSurface }]}
            >
              {formatNumber(data.viewCount)}
            </Text>
            <Text
              variant='bodySmall'
              style={[
                styles.metricLabel,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              Views
            </Text>
          </View>

          {/* Acceptance Rate */}
          <View style={styles.metric}>
            <View style={styles.metricIcon}>
              <ThumbsUp size={12} color={theme.colors.tertiary} />
            </View>
            <Text
              variant='bodySmall'
              style={[styles.metricValue, { color: theme.colors.onSurface }]}
            >
              {Math.round(data.acceptanceRate * 100)}%
            </Text>
            <Text
              variant='bodySmall'
              style={[
                styles.metricLabel,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              Accepted
            </Text>
          </View>

          {/* Average Rating */}
          <View style={styles.metric}>
            <View style={styles.metricIcon}>
              <Star size={12} color={getRatingColor(data.averageRating)} />
            </View>
            <Text
              variant='bodySmall'
              style={[
                styles.metricValue,
                { color: getRatingColor(data.averageRating) },
              ]}
            >
              {data.averageRating.toFixed(1)}
            </Text>
            <Text
              variant='bodySmall'
              style={[
                styles.metricLabel,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              Rating
            </Text>
          </View>

          {/* Similar User Engagement */}
          <View style={styles.metric}>
            <View style={styles.metricIcon}>
              <Users size={12} color={theme.colors.outline} />
            </View>
            <Text
              variant='bodySmall'
              style={[styles.metricValue, { color: theme.colors.onSurface }]}
            >
              {Math.round(data.similarUserEngagement * 100)}%
            </Text>
            <Text
              variant='bodySmall'
              style={[
                styles.metricLabel,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              Peer Engagement
            </Text>
          </View>
        </View>

        {/* Insights */}
        <View style={styles.insights}>
          <View style={styles.insightItem}>
            <TrendingUp size={12} color={theme.colors.primary} />
            <Text
              variant='bodySmall'
              style={[
                styles.insightText,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              {getEngagementLevel(data.acceptanceRate)} engagement from similar
              users
            </Text>
          </View>

          {data.averageRating >= 4.0 && (
            <View style={styles.insightItem}>
              <Star size={12} color={getRatingColor(data.averageRating)} />
              <Text
                variant='bodySmall'
                style={[
                  styles.insightText,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                Highly rated by learners like you
              </Text>
            </View>
          )}

          {data.performanceScore >= 0.8 && (
            <View style={styles.insightItem}>
              <Target
                size={12}
                color={getPerformanceColor(data.performanceScore)}
              />
              <Text
                variant='bodySmall'
                style={[
                  styles.insightText,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                Top performing recommendation for your profile
              </Text>
            </View>
          )}
        </View>

        {/* Confidence Indicator */}
        <View style={styles.confidenceSection}>
          <Text
            variant='bodySmall'
            style={[
              styles.confidenceLabel,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            AI Confidence:
          </Text>
          <Chip
            mode='outlined'
            textStyle={[
              styles.confidenceChipText,
              { color: getPerformanceColor(recommendation.confidence) },
            ]}
            style={[
              styles.confidenceChip,
              { borderColor: getPerformanceColor(recommendation.confidence) },
            ]}
          >
            {Math.round(recommendation.confidence * 100)}%
          </Chip>
        </View>
      </Card.Content>
    </Card>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
  },
  content: {
    paddingVertical: 12,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  title: {
    fontWeight: '600',
    fontSize: 11,
  },
  section: {
    marginBottom: 12,
  },
  metricHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 6,
  },
  performanceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  performanceBar: {
    flex: 1,
    height: 6,
    borderRadius: 3,
  },
  performanceScore: {
    fontSize: 12,
    fontWeight: '600',
    minWidth: 35,
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 12,
  },
  metric: {
    flex: 1,
    minWidth: 60,
    alignItems: 'center',
    gap: 4,
  },
  metricIcon: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: 'rgba(78, 205, 196, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  metricValue: {
    fontSize: 14,
    fontWeight: '600',
  },
  metricLabel: {
    fontSize: 9,
    textAlign: 'center',
  },
  insights: {
    gap: 6,
    marginBottom: 12,
  },
  insightItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  insightText: {
    fontSize: 10,
    flex: 1,
  },
  confidenceSection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  confidenceLabel: {
    fontSize: 10,
  },
  confidenceChip: {
    height: 20,
  },
  confidenceChipText: {
    fontSize: 9,
    fontWeight: '600',
  },
});
